import { Link, useLocation } from "react-router-dom";
import { useAsync } from "react-use";
import { useDisclosure } from "@mantine/hooks";
import {
  Sun, Moon, User, ListTodo, Upload, Film, Image as ImageIcon, ClipboardCheck, ListVideo, Settings, Menu as MenuIcon,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { Box, Text, Anchor, ActionIcon, Drawer, Group } from "@mantine/core";
import lectoryImg from "../assets/lectory.png";
import { isAdmin, useReviewAccess } from "../AuthContext";
import { getUiConfig } from "../api";
import { useThemeContext } from "../ThemeContext";
import { PlatformStatus } from "./PlatformStatus";
import "./TopMenu.css";

interface NavLink {
  href: string;
  label: string;
  icon: LucideIcon;
  external?: boolean;
}

/** A link, or a heading that starts a group of links in the sidebar. */
type NavItem = NavLink | { section: string };

function useNavLinks(): NavItem[] {
  const { value: isAdminValue } = isAdmin();
  const { value: hasReviewAccess } = useReviewAccess();

  // The montage group runs in the order a lecture goes through it.
  const links: NavItem[] = [
    { href: "/profile", label: "Профиль", icon: User },
    { section: "Монтаж" },
    { href: "/editor", label: "Автомонтаж", icon: Film },
    { href: "/previews", label: "Обложки", icon: ImageIcon },
    { href: "/tasks/submit", label: "Публикация", icon: Upload },
    { section: "Утилиты" },
    { href: "/tasks", label: "Очередь задач", icon: ListTodo },
    { href: "/playlists", label: "Плейлисты", icon: ListVideo },
  ];

  if (hasReviewAccess) {
    links.push({ href: "/review", label: "Проверка", icon: ClipboardCheck });
  }
  if (isAdminValue) {
    links.push({ href: "/admin", label: "Админка", icon: Settings });
  }
  return links;
}

export function TopBar() {
  const [drawerOpened, { open: openDrawer, close: closeDrawer }] = useDisclosure(false);
  const { value: config } = useAsync(getUiConfig, []);
  const { theme, toggleTheme } = useThemeContext();
  const links = useNavLinks();
  const location = useLocation();

  const isActive = (href: string) => {
    if (href === "/tasks") {
      return location.pathname === "/tasks"
        || (location.pathname.startsWith("/tasks/") && !location.pathname.startsWith("/tasks/submit"));
    }
    return location.pathname.startsWith(href);
  };

  return (
    <>
      <Box component="header" className="top-bar">
        <div className="top-bar-brand">
          <ActionIcon
            variant="subtle"
            size="lg"
            color="gray"
            onClick={openDrawer}
            aria-label="Открыть меню навигации"
          >
            <MenuIcon size={20} />
          </ActionIcon>
          
          <Group gap="xs" style={{ cursor: "pointer", userSelect: "none" }} onClick={openDrawer}>
            <Box className="top-bar-logo">
              <img src={lectoryImg} alt="Лекторий ФПМИ" />
            </Box>
            <Text fw={600} fz={18} lh={1}>Лекторий ФПМИ</Text>
          </Group>
        </div>

        <div className="top-bar-actions">
          {config?.bug_report_url && (
            <Anchor
              href={config.bug_report_url}
              target="_blank"
              rel="noopener noreferrer"
              c="dimmed"
              fw={500}
              fz="sm"
              underline="never"
            >
              Сообщить об ошибке
            </Anchor>
          )}
          <PlatformStatus />
          <ActionIcon
            variant="light"
            size="lg"
            color="gray"
            onClick={toggleTheme}
            aria-label={theme === 'light' ? 'Switch to dark theme' : 'Switch to light theme'}
          >
            {theme === 'light' ? <Moon size={18} /> : <Sun size={18} />}
          </ActionIcon>
        </div>
      </Box>

      <Drawer
        opened={drawerOpened}
        onClose={closeDrawer}
        size={270}
        padding="md"
        title={
          <Group gap="xs" align="center">
            <Box className="top-bar-logo">
              <img src={lectoryImg} alt="Лекторий ФПМИ" />
            </Box>
            <Text fw={600} fz={17} lh={1}>Лекторий ФПМИ</Text>
          </Group>
        }
        overlayProps={{ backgroundOpacity: 0.35, blur: 4 }}
      >
        <nav className="sidebar-nav" style={{ padding: "8px 0" }}>
          {links.map(link => {
            if ("section" in link) return <div key={link.section} className="sidebar-section-title">{link.section}</div>;
            const active = !link.external && isActive(link.href);
            const className = `sidebar-link${active ? " sidebar-link-active" : ""}`;
            const Icon = link.icon;
            const content = (
              <>
                <Icon size={18} className="sidebar-link-icon" />
                <span>{link.label}</span>
              </>
            );
            return link.external ? (
              <a key={link.label} href={link.href} className={className} onClick={closeDrawer}>
                {content}
              </a>
            ) : (
              <Link key={link.label} to={link.href} className={className} onClick={closeDrawer}>
                {content}
              </Link>
            );
          })}
        </nav>
      </Drawer>
    </>
  );
}